import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
  alpha,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import GavelIcon from "@mui/icons-material/Gavel";
import BuildIcon from "@mui/icons-material/Build";
import PropertyRulesPanel, {
  rulesReportSections,
} from "../../../components/PropertyRulesPanel";
import {
  openPropertyReport,
  reportDate,
  reportMoney,
} from "../../../utils/propertyReport";

const VIOLATION_TONE = {
  OPEN: { color: "#b45309", bg: "#fffbeb", label: "Open" },
  ACKNOWLEDGED: { color: "#1d4ed8", bg: "#eff6ff", label: "Acknowledged" },
  RESOLVED: { color: "#15803d", bg: "#f0fdf4", label: "Resolved" },
  ESCALATED: { color: "#b91c1c", bg: "#fef2f2", label: "Escalated" },
};

const MAINTENANCE_TONE = {
  pending: { color: "#b45309", bg: "#fffbeb" },
  accepted: { color: "#15803d", bg: "#f0fdf4" },
  completed: { color: "#15803d", bg: "#f0fdf4" },
  rejected: { color: "#b91c1c", bg: "#fef2f2" },
};

const toneFor = (map, key, fallback) =>
  map[key] || map[String(key || "").toLowerCase()] || fallback;

const fmt = (value) => (value ? reportDate(value) : "—");

function EmptyState({ icon, title, body }) {
  return (
    <Box sx={{ textAlign: "center", py: 5, px: 2 }}>
      <Box sx={{ color: "#c3c8d4", mb: 1 }}>{icon}</Box>
      <Typography sx={{ fontWeight: 700, mb: 0.5 }}>{title}</Typography>
      <Typography variant="body2" color="text.secondary">
        {body}
      </Typography>
    </Box>
  );
}

/**
 * The full record for the flat a tenant currently rents.
 *
 * The dashboard card carries four signals and nothing more — everything that
 * cannot fit there without turning the card into a wall of text lives behind
 * these tabs, and everything at all lives in the PDF the header button builds.
 */
export default function RentalDetailDialog({
  open,
  onClose,
  rental,
  rules,
  rulesLoading,
  violations = [],
  maintenance = [],
  accent = "#2563eb",
  initialTab = 0,
}) {
  const [tab, setTab] = useState(initialTab);

  // Reopening from "See everything" must land on the tab the caller asked
  // for, not on whatever was left selected last time.
  useEffect(() => {
    if (open) setTab(initialTab);
  }, [open, initialTab]);

  const openViolations = useMemo(
    () => violations.filter((v) => v.status !== "RESOLVED").length,
    [violations]
  );
  const openMaintenance = useMemo(
    () =>
      maintenance.filter((m) =>
        ["pending", "accepted", "in-progress"].includes(
          String(m.status || "").toLowerCase()
        )
      ).length,
    [maintenance]
  );

  if (!rental) return null;

  const handleDownload = () => {
    const opened = openPropertyReport({
      title: rental.flatName || "My Rental",
      subtitle: rental.mapAddress || "Location not specified",
      accent,
      stats: [
        { label: "Monthly rent", value: reportMoney(rental.flatRent) },
        { label: "Violations logged", value: String(violations.length) },
        { label: "Open violations", value: String(openViolations) },
        {
          label: "Maintenance requests",
          value: `${maintenance.length} (${openMaintenance} open)`,
        },
      ],
      sections: [
        {
          heading: "Property",
          rows: [
            { label: "Flat", value: rental.flatName },
            { label: "Address", value: rental.mapAddress },
            { label: "Monthly rent", value: reportMoney(rental.flatRent) },
          ],
        },
        {
          heading: "Owner",
          rows: [
            { label: "Name", value: rental.landlordName },
            { label: "Email", value: rental.landlordEmail },
            { label: "Phone", value: rental.landlordPhone },
          ],
        },
        ...rulesReportSections(rules),
        {
          heading: "Agreement violations",
          note: `${violations.length} logged • ${openViolations} still open`,
          type: "table",
          columns: ["Date", "Reason", "Severity", "Status", "Details"],
          empty: "No violations have been logged against you on this property.",
          rows: violations.map((v) => [
            fmt(v.createdAt),
            v.reason,
            v.severity,
            v.status,
            [
              v.description,
              v.apology?.message ? `Your response: ${v.apology.message}` : "",
              v.apology?.decision && v.apology.decision !== "PENDING"
                ? `Owner ${String(v.apology.decision).toLowerCase()} it${
                    v.apology.decisionNote ? ` — ${v.apology.decisionNote}` : ""
                  }`
                : "",
            ]
              .filter(Boolean)
              .join(" | "),
          ]),
        },
        {
          heading: "Maintenance requests",
          note: `${maintenance.length} raised • ${openMaintenance} open`,
          type: "table",
          columns: ["Date", "Category", "Urgency", "Status", "Description"],
          empty: "No maintenance requests raised for this property.",
          rows: maintenance.map((m) => [
            fmt(m.createdAt),
            m.category,
            m.urgency,
            m.status,
            m.description,
          ]),
        },
      ],
    });

    if (!opened) {
      window.alert(
        "Please allow pop-ups for this site to download the rental report."
      );
    }
  };

  const overviewRows = [
    { label: "Flat", value: rental.flatName || "—" },
    { label: "Address", value: rental.mapAddress || "—" },
    { label: "Monthly rent", value: reportMoney(rental.flatRent) },
    { label: "Owner", value: rental.landlordName || "—" },
    { label: "Owner email", value: rental.landlordEmail || "—" },
    { label: "Owner phone", value: rental.landlordPhone || "—" },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: "18px", overflow: "hidden" } }}
    >
      <Box
        sx={{
          px: { xs: 2, sm: 3 },
          py: 2,
          background: `linear-gradient(120deg, ${accent} 0%, ${alpha(
            accent,
            0.75
          )} 100%)`,
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.5,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 800, fontSize: 19 }} noWrap>
            {rental.flatName || "My Rental"}
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.85 }} noWrap>
            {rental.mapAddress || "Location not specified"}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Button
            size="small"
            startIcon={<PictureAsPdfIcon />}
            onClick={handleDownload}
            sx={{
              display: { xs: "none", sm: "inline-flex" },
              textTransform: "none",
              fontWeight: 700,
              color: "#fff",
              borderRadius: "999px",
              px: 1.8,
              bgcolor: "rgba(255,255,255,0.18)",
              "&:hover": { bgcolor: "rgba(255,255,255,0.3)" },
            }}
          >
            Full Report
          </Button>
          <IconButton
            onClick={handleDownload}
            sx={{ color: "#fff", display: { xs: "inline-flex", sm: "none" } }}
          >
            <PictureAsPdfIcon />
          </IconButton>
          <IconButton onClick={onClose} sx={{ color: "#fff" }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      <Tabs
        value={tab}
        onChange={(_, next) => setTab(next)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          px: 1,
          borderBottom: "1px solid #eceff5",
          "& .MuiTab-root": { textTransform: "none", fontWeight: 700 },
          "& .Mui-selected": { color: `${accent} !important` },
          "& .MuiTabs-indicator": { backgroundColor: accent },
        }}
      >
        <Tab label="Overview" />
        <Tab label="Rules & Terms" />
        <Tab
          label={
            violations.length ? `Violations (${violations.length})` : "Violations"
          }
        />
        <Tab
          label={
            maintenance.length
              ? `Maintenance (${maintenance.length})`
              : "Maintenance"
          }
        />
      </Tabs>

      <DialogContent sx={{ p: { xs: 2, sm: 3 }, bgcolor: "#fbfcfe" }}>
        {tab === 0 && (
          <Stack spacing={1}>
            {overviewRows.map((row) => (
              <Box
                key={row.label}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 2,
                  py: 1.1,
                  borderBottom: "1px solid #eef1f6",
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {row.label}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 700, textAlign: "right" }}
                >
                  {row.value}
                </Typography>
              </Box>
            ))}
          </Stack>
        )}

        {tab === 1 && (
          <PropertyRulesPanel
            rules={rules}
            loading={rulesLoading}
            accent={accent}
          />
        )}

        {tab === 2 &&
          (violations.length === 0 ? (
            <EmptyState
              icon={<GavelIcon sx={{ fontSize: 44 }} />}
              title="Clean record"
              body="No agreement violations have been logged against you on this property."
            />
          ) : (
            <Stack spacing={1.4}>
              {violations.map((v) => {
                const tone = toneFor(
                  VIOLATION_TONE,
                  v.status,
                  VIOLATION_TONE.OPEN
                );

                return (
                  <Paper
                    key={v._id}
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: "14px",
                      border: "1px solid #eceff5",
                      borderLeft: `4px solid ${tone.color}`,
                      bgcolor: "#fff",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 0.8,
                        mb: 1,
                      }}
                    >
                      <Typography sx={{ fontWeight: 700, mr: 0.5 }}>
                        {v.reason}
                      </Typography>
                      <Chip
                        size="small"
                        label={tone.label || v.status}
                        sx={{
                          fontWeight: 700,
                          fontSize: 11,
                          color: tone.color,
                          bgcolor: tone.bg,
                        }}
                      />
                      {v.severity === "HIGH" && (
                        <Chip
                          size="small"
                          label="High severity"
                          sx={{
                            fontWeight: 700,
                            fontSize: 11,
                            color: "#b91c1c",
                            bgcolor: "#fef2f2",
                          }}
                        />
                      )}
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ ml: "auto" }}
                      >
                        {fmt(v.createdAt)}
                      </Typography>
                    </Box>

                    <Typography variant="body2" sx={{ color: "#37425c" }}>
                      {v.description}
                    </Typography>

                    {v.apology?.message && (
                      <>
                        <Divider sx={{ my: 1.4 }} />
                        <Typography variant="caption" color="text.secondary">
                          Your response
                        </Typography>
                        <Typography variant="body2" sx={{ fontStyle: "italic" }}>
                          “{v.apology.message}”
                        </Typography>
                        {v.apology.decision &&
                          v.apology.decision !== "PENDING" && (
                            <Typography
                              variant="body2"
                              sx={{
                                mt: 0.6,
                                fontWeight: 600,
                                color:
                                  v.apology.decision === "ACCEPTED"
                                    ? "#15803d"
                                    : "#b91c1c",
                              }}
                            >
                              Owner {v.apology.decision.toLowerCase()} it
                              {v.apology.decisionNote
                                ? ` — ${v.apology.decisionNote}`
                                : ""}
                            </Typography>
                          )}
                      </>
                    )}
                  </Paper>
                );
              })}
            </Stack>
          ))}

        {tab === 3 &&
          (maintenance.length === 0 ? (
            <EmptyState
              icon={<BuildIcon sx={{ fontSize: 44 }} />}
              title="No requests yet"
              body="Nothing has been reported for repair on this property."
            />
          ) : (
            <Stack spacing={1.4}>
              {maintenance.map((m) => {
                const tone = toneFor(MAINTENANCE_TONE, m.status, {
                  color: "#64748b",
                  bg: "#f1f5f9",
                });

                return (
                  <Paper
                    key={m._id}
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: "14px",
                      border: "1px solid #eceff5",
                      borderLeft: `4px solid ${tone.color}`,
                      bgcolor: "#fff",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 0.8,
                        mb: 1,
                      }}
                    >
                      <Typography sx={{ fontWeight: 700, mr: 0.5 }}>
                        {m.category || "Maintenance"}
                      </Typography>
                      <Chip
                        size="small"
                        label={m.status}
                        sx={{
                          fontWeight: 700,
                          fontSize: 11,
                          color: tone.color,
                          bgcolor: tone.bg,
                          textTransform: "capitalize",
                        }}
                      />
                      {m.urgency && (
                        <Chip
                          size="small"
                          variant="outlined"
                          label={m.urgency}
                          sx={{ fontSize: 11, textTransform: "capitalize" }}
                        />
                      )}
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ ml: "auto" }}
                      >
                        {fmt(m.createdAt)}
                      </Typography>
                    </Box>

                    <Typography variant="body2" sx={{ color: "#37425c" }}>
                      {m.description}
                    </Typography>

                    {m.reply && (
                      <>
                        <Divider sx={{ my: 1.4 }} />
                        <Typography variant="caption" color="text.secondary">
                          Owner's reply
                        </Typography>
                        <Typography variant="body2">{m.reply}</Typography>
                      </>
                    )}
                  </Paper>
                );
              })}
            </Stack>
          ))}
      </DialogContent>
    </Dialog>
  );
}
